// src/pages/BlogPost.js
import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { collection, onSnapshot, orderBy, query, where } from "firebase/firestore";
import { db } from "../firebase";
import Seo from "../components/Seo";

const toDate = (value) => {
  if (!value) return null;
  if (value instanceof Date) return value;
  if (typeof value?.toDate === "function") return value.toDate();
  if (typeof value?.seconds === "number") return new Date(value.seconds * 1000);
  return null;
};

const formatDate = (value) => {
  const date = toDate(value);
  if (!date) return "";
  return date.toLocaleDateString(undefined, {
    year: "numeric",
    month: "long",
    day: "numeric"
  });
};

const toSummary = (text, maxLen = 160) => {
  const stripped = (text || "")
    .replace(/#{1,6}\s/g, "")
    .replace(/\*\*([^*]+)\*\*/g, "$1")
    .replace(/\*([^*]+)\*/g, "$1")
    .replace(/\[([^\]]+)\]\([^)]+\)/g, "$1")
    .replace(/\s+/g, " ")
    .trim();
  if (!stripped) return "";
  if (stripped.length <= maxLen) return stripped;
  return `${stripped.slice(0, maxLen).trim()}…`;
};

const WORDS_PER_MINUTE = 200;

const getReadingTimeMinutes = (text) => {
  const stripped = (text || "")
    .replace(/#{1,6}\s/g, "")
    .replace(/\*\*([^*]+)\*\*/g, "$1")
    .replace(/\*([^*]+)\*/g, "$1")
    .replace(/\[([^\]]+)\]\([^)]+\)/g, "$1")
    .replace(/`[^`]*`/g, " ");
  const words = stripped.split(/\s+/).filter(Boolean).length;
  const minutes = Math.max(1, Math.ceil(words / WORDS_PER_MINUTE));
  return minutes;
};

const BlogPost = () => {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    if (!slug) {
      setNotFound(true);
      setLoading(false);
      return;
    }

    const q = query(
      collection(db, "blogPosts"),
      where("slug", "==", slug),
      where("published", "==", true)
    );
    const unsub = onSnapshot(
      q,
      (snapshot) => {
        if (snapshot.empty) {
          setPost(null);
          setNotFound(true);
        } else {
          const doc = snapshot.docs[0];
          setPost({ id: doc.id, ...doc.data() });
          setNotFound(false);
        }
        setLoading(false);
      },
      () => {
        setPost(null);
        setNotFound(true);
        setLoading(false);
      }
    );
    return () => unsub();
  }, [slug]);

  if (loading) {
    return (
      <main className="up-page container">
        <div className="up-content-block">
          <p className="up-hint">Loading…</p>
        </div>
      </main>
    );
  }

  if (notFound || !post) {
    return (
      <main className="up-page container">
        <Seo title="Post not found" path="/blog/not-found" noindex />
        <div className="up-content-block">
          <h1 className="up-page-title">Post not found</h1>
          <p className="up-page-subtitle">This blog post may have been removed or the URL is incorrect.</p>
          <Link to="/blog" className="up-link">← Back to Blog</Link>
        </div>
      </main>
    );
  }

  const canonicalPath = `/blog/${encodeURIComponent(post.slug)}`;
  const description = toSummary(post.content);

  return (
    <main className="up-page container">
      <Seo
        title={post.title || "Blog post"}
        description={description}
        path={canonicalPath}
        image={post.image || undefined}
        type="article"
      />
      <article className="up-content-block blog-post">
        <Link to="/blog" className="blog-post-back up-link">← Back to Blog</Link>
        <header className="blog-post-header">
          <time className="blog-post-date" dateTime={toDate(post.createdAt)?.toISOString()}>
            {formatDate(post.createdAt)}
          </time>
          <h1 className="blog-post-title">{post.title || "Untitled"}</h1>
          <p className="blog-post-reading-time">
            {getReadingTimeMinutes(post.content)} min read
          </p>
        </header>
        {post.image && (
          <div className="blog-post-image-wrap">
            <img src={post.image} alt="" className="blog-post-image" />
          </div>
        )}
        <div className="blog-post-body blog-post-markdown">
          <ReactMarkdown remarkPlugins={[remarkGfm]}>
            {post.content || ""}
          </ReactMarkdown>
        </div>
      </article>
    </main>
  );
};

export default BlogPost;
